import { AfterViewInit, Component, ComponentFactoryResolver, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { AuthActions } from '@app/modules/auth/store/auth.actions';
import { Observable, Subscription } from 'rxjs';
import { Navigate } from '@ngxs/router-plugin';
import { UserModel } from '@app/modules/user/model/user';
import { UserState } from '@app/modules/user/store/user.state';
import { NotificationsState } from '@app/modules/notifications/store/notifications.state';
import { NotificationsActions } from '@app/modules/notifications/store/notifications.actions';
import { UserActions } from '@app/modules/user/store/user.actions';
import { AuthState } from '@app/modules/auth/store/auth.state';
import { IPasswordChange } from '@app/modules/auth/interface/password-change';

@Component({
  selector: 'app-private-layout',
  templateUrl: './private-layout.component.html',
  styleUrls: ['./private-layout.component.sass']
})
export class PrivateLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private _subscription = new Subscription();

  @Select(AuthState.loading) public authLoading$: Observable<boolean>;

  @Select(UserState.loading) public userLoading$: Observable<boolean>;

  @Select(NotificationsState.loading) public notificationLoading$: Observable<boolean>;

  @Select(UserState.model) public user$: Observable<UserModel>;

  @Select(UserState.sidebarVisible) public userSidebarVisible$: Observable<boolean>;



  @Select(NotificationsState.newNotificationCount) public newNotificationCount$: Observable<number>;

  @ViewChild('notificationSidebar', { read: ViewContainerRef }) private notificationSidebarRef: ViewContainerRef;


  /**
   * @param store
   * @param vcRef
   * @param cfr
   * @param actions
   */
  constructor(
    private readonly store: Store,
    private readonly vcRef: ViewContainerRef,
    private readonly cfr: ComponentFactoryResolver,
    private readonly actions: Actions,
  ) {}

  public ngOnInit(): void {
    this.subscribeOnLogoutSuccess();
  }

  /**
   * The notification sidebar mounts into a @ViewChild container, which only
   * exists AFTER the view initializes — loading it from ngOnInit worked by
   * accident (the dynamic import was usually slower than view init) and died
   * with an unhandled rejection whenever it wasn't. AfterViewInit is the
   * guaranteed-safe point, and a failed chunk load (stale tab after a
   * rebuild) must never take the whole layout down with it.
   */
  public ngAfterViewInit(): void {
    this.loadNotificationSidebarComponent().catch((error) =>
      console.error('Notification sidebar failed to load; the rest of the layout is unaffected.', error),
    );
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public logout(): void {
    this.store.dispatch(new AuthActions.Logout());
  }

  public passwordChange(value: IPasswordChange): void {
    this.store.dispatch(new AuthActions.PasswordChange(value));
  }

  public changeNotificationSidebarVisible(value: boolean): void {
    const sidebarVisible = this.store.selectSnapshot(NotificationsState.sidebarVisible);

    if (value !== sidebarVisible) {
      this.store.dispatch(new NotificationsActions.ChangeSidebarVisible(value));
    }
  }

  public changeUserSidebarVisible(value: boolean): void {
    const sidebarVisible = this.store.selectSnapshot(UserState.sidebarVisible);

    if (value !== sidebarVisible) {
      this.store.dispatch(new UserActions.ChangeSidebarVisible(value));
    }
  }

  /** Both outcomes: the local session is already gone, so leave the app. */
  private subscribeOnLogoutSuccess(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(AuthActions.LogoutSuccess, AuthActions.LogoutFailed))
        .subscribe(() => {
          this.store.dispatch(new Navigate(['/auth/login']));
          window.location.reload(); // @todo use meta reducer
        }),
    );
  }

  private async loadNotificationSidebarComponent(): Promise<void> {
    const { NotificationSidebarComponent } = await import('@app/modules/notifications/part/notification-sidebar/notification-sidebar.component');

    if (!this.notificationSidebarRef) {
      return;
    }
    this.vcRef.clear();
    this.notificationSidebarRef.createComponent(
      this.cfr.resolveComponentFactory(NotificationSidebarComponent)
    );
  }

}
